<template>
    <div class="terms">
        <div class="container"  >
            <div class="title">
                <h1>Reolink Subscription Terms</h1>
            </div>
            <div class="body">
                <p>Last updated on November 16, 2024.</p>
                <p>These reolink Subscription Terms (the "Subscription Terms" or "Terms") are supplements to reolink <a class='text-link' href='https://reolink.com/terms-conditions/' target='_blank'>Terms and Conditions</a>, reolink <a class='text-link' href='https://reolink.com/privacy-policy/' target='_blank'>Privacy Policy</a>, <a class='text-link' href='/terms-of-use/' target='_blank'>Terms of Use of reolink Cloud Service</a> and/or other relevant terms. We (Reolink Innovation Limited and our affiliates, also referred to as "we" or "reolink") provide subscription services (the "Subscription Service") such as the reolink Cloud Service and plans to be purchased to get access to the Subscription Services (the "Plan") to you subject to these Terms.</p>
                <p>By purchasing any of the Plans, accessing or otherwise using any of the Subscription Services, you agree to be bound by these Terms. If you are not willing to be bound by these Terms, you should not purchase any of the Plans, or access or use any of the Subscription Services.</p>

                <h3>1. Eligibility Requirements</h3>
                <p>To be eligible to purchase, access and use the Subscription Services, you must meet the following criteria ("Eligibility Requirements"):</p>
                <p class="list">1. As the Subscription Services and Plans may be limited to certain territories/product models, you must first check if such Subscription Services are applicable for the product model you would like to purchase or within the territory you have chosen ("Territory") on the detail page on our website or application.</p>
                <p class="list">2. You must be at least 18 years of age or the age of legal majority in your Territory, and capable of entering into a binding agreement;</p>
                <p class="list">3. You must have an registered account in good standing with reolink;</p>
                <p class="list">4. You must have bind an authentic reolink device to your reolink account;</p>
                <p class="list">5. You must meet any additional qualifications or adhere to any limitations disclosed with the specific Subscription Services as displayed on the detail page on our website or application, which we may update from time to time.</p>

                <h3>2. Subscriptions</h3>
                <p>We provide different kinds of Plans such as Cloud Plans and Add-on Plans under the reolink Cloud Service for you to subscribe. You may purchase the subscription for any of these Plans for a fee (the "Fee") and for the period of time (the "Period") you have elected from the available options. The detailed standards and options for these Plans will be displayed on the relevant pages of our website/application unless otherwise specified.</p>

                <h3>3. Free Trials</h3>
                <p>Depending on the specific Plans you have chosen to subscribe, you may be enrolled in a free trial of such Plan (Add-on Plans shall be excluded). These free trials are intended to allow you to try the Plans with your reolink device. We reserves the right, in our discretion, to determine you free trial eligibility and to modify or cancel a free trial or a free trial offer at any time.</p>

                <h3>4. Access and Renewal</h3>
                <p>Your ability to use the Plans and access to the Subscription Services commences on the date we make the Plans available to you following your purchase of the subscription of such Plans, and continues until the end of the applicable Period.</p>
                <p>In order to continue to use the Plan and/or the Subscription Services after your Period has expired, you will need to purchase or redeem a new Plan. You are required to maintain accurate and up-to-date payment information.</p>
                <p>If you have provided your authorization for automatic payment in your Paypal or other applicable account for us, we will automatically renew your Subscription for a new Period by charging the associated Fee to the payment method you provided. If such automatic payment fails for any reason, you shall complete the payment manually to keep your Subscription active.</p>
                <p>You can opt-out of automatic renewal before the Fee for the next Period is automatically and successfully deducted by reolink (as applicable).</p>
                <p>reolink assumes no responsibility or liability if your subscribed Plan fails to renew or otherwise expires because of outdated or incorrect payment information.</p>
                <p>Note: Automatic renewal does not apply to all the Add-on Plans. You shall purchase a new subscription for such Add-on Plan upon its expiry.</p>

                <h3>5. Cancellation</h3>
                <p>You may cancel your subscription to the Plans and/or the Subscription Services, at any time and for any reason by following the instructions included in your Plan purchase, redemption, or renewal confirmation email. You may also contact <a class='text-link' :href="config.support_team" target='_blank'>Support Team</a> to cancel your Plan or Subscription Services. However, if you do so, reolink will not refund any Fees for the Plans and/or the Subscription Services, as applicable, that have been validly charged, debited or otherwise billed to your account as of the date of such termination.</p>
                <p>After you cancel your subscription to the Plans and/or the Subscription Services, you will continue to have access to the Plans and/or the Subscription Services, as applicable, until the end of your current Period. At that time, your subscription and access to the Plans and/or the Subscription Services, as applicable, will end.</p>

                <h3>6. Availability and Modifications</h3>
                <p>reolink reserves the right to modify, offer additional, or cease offering Plans and/or Subscription Services at any time in its sole discretion, including but not limited to changes in Fees, modifying the availability of and Eligibility Requirements for certain Plans and/or Subscription Services, or modifying the features and functionality of certain Subscription Services. If we cease offering your current Plan, your access to the Subscription Services will continue until the end of your then-current Period. Any changes to the Plans or Fees will be effective upon your next billing cycle, and will not apply retroactively or to the remainder of your current Period. In the event we do modify your current Period or Fee, you may opt-out of these changes by disabling automatic renewal of your Plan, or cancelling your Plan by following the instructions as stipulated in section 5 Cancellation.</p>

                <h3>7. Changes to Subscription terms</h3>
                <p>We may modify these Subscription Terms from time to time. If we make material changes to these Subscription Terms as it applies to your currently active Plan, then we will supply you with appropriate notice (e.g., in-app notification, email, statement, etc.) of those changes. Revised Subscription Terms will become effective upon the next renewal of your Plan and your continued use of the Subscription Services will constitute your acceptance of the revised terms. If you are not willing to agree to the revisions, you may cancel your Plan by following the instructions as stipulated in section 5 Cancellation.
                </p>

            </div>
        </div>
    </div>
</template>
<script lang="ts">
import Vue from 'vue';
import { config } from 'CONFIG';
import { ensureGdprCookies } from '@/common/js/gdprCookie';
import nProgress from 'nprogress';

export default Vue.extend({

    data () {

        return {
            config
        };

    },
    mounted() {

        nProgress.done();

    },
    computed: {
        client () {

            return this.config.oem;

        },

        lang(): string {

            return this.$store.getters.lang;

        },
        userStatus () {

            return this.$store.state.user.status;

        }
    },
    watch: {

        async userStatus () {

            await ensureGdprCookies( this.$i18n.locale );

        }

    }
});
</script>
<style lang="less" scoped>
.terms{
    background: #f7f8f9;
    padding: .4rem 0;
    .container{
        width:9.6rem;
        margin:0 auto;
        background: #fff;
        .title{
            padding:.48rem 0;
            background-color: #5A5A5A;
            margin-bottom:.64rem;
            h1{
                line-height: 1.4;
                font-size: .36rem;
                text-align: center;
                color:white;
            }
        }
        .body{
            margin:0 .5rem;
            padding-top:.4rem;
            padding-bottom:.8rem;
            h2{
                font-size: .24rem;
                line-height: 1.4;
                margin-top: .8rem;
            }
            h3{
                margin-top: .3rem;
                font-size:.2rem;
                line-height: 1.6;
            }
            p{
                font-size: .18rem;
                line-height: 2;
                margin-top: .24rem;
            }
            p.list{
                margin-top:.2rem;
                padding-left:.3rem;
            }
        }

    }
}
@media @mobile {
    .terms{
        background: #f7f8f9;
        padding: 0;
        width: 100%;
        .container{
            width:100%;
            margin:0 auto;
            background: #fff;
            .title{
                padding:.48rem 0;
                background-color: #5A5A5A;
                h1{
                    line-height: 1.4;
                    font-size: .50rem;
                    text-align: center;
                    color:white;
                    width: 6.9rem;
                    margin:0 auto;
                    text-align: center;
                }
            }
            .body{
                margin:0 .5rem;
                margin-top:.24rem;
                padding-top:0;
                padding-bottom:.8rem;
                h2{
                    font-size: .40rem;
                    line-height: 1.4;
                    margin-top: 1.08rem;
                }
                h3{
                    margin-top: .64rem;
                    font-size:.34rem;
                    line-height: 1.6;
                }
                p{
                    font-size: .30rem;
                    line-height: 2;
                    margin-top: .40rem;
                }
                p.list{
                    margin-top:.32rem;
                    padding-left:.4rem;
                }
            }

        }
    }
}
</style>
